import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api } from '../../../shared/api/client';

/**
 * Lado reverso do vínculo RC↔Projeto (card [pusd8Icv]): a partir de um projeto
 * do cliente, gerenciar as Requisições de Compra vinculadas. Mesma tabela
 * associativa `ClienteProjetoRequisicaoCompra` do lado RC ([uJoKy8Qk]).
 */
export interface ProjetoRcVinculada {
  requisicaoCompraCodigo: number;
  requisicaoCompraReferencia: string;
  status: string;
  saldo: number;
}

const base = (clientId: number, projectId: number): string =>
  `/v1/clients/${clientId}/projects/${projectId}/requisicoes-compra`;

export async function listProjetoRequisicoes(
  clientId: number,
  projectId: number,
): Promise<ProjetoRcVinculada[]> {
  const { data } = await api.get<{ data: ProjetoRcVinculada[] }>(base(clientId, projectId));
  return data.data;
}

export async function linkProjetoRequisicao(
  clientId: number,
  projectId: number,
  requisicaoCompraCodigo: number,
): Promise<void> {
  await api.post(base(clientId, projectId), { requisicaoCompraCodigo });
}

export async function unlinkProjetoRequisicao(
  clientId: number,
  projectId: number,
  requisicaoCompraCodigo: number,
): Promise<void> {
  await api.delete(`${base(clientId, projectId)}/${requisicaoCompraCodigo}`);
}

const queryKey = (clientId: number, projectId: number): readonly unknown[] => [
  'client-projects',
  clientId,
  projectId,
  'requisicoes',
];

export function useProjetoRequisicoes(
  clientId: number,
  projectId: number | null,
): UseQueryResult<ProjetoRcVinculada[]> {
  return useQuery({
    queryKey: queryKey(clientId, projectId ?? 0),
    queryFn: () => listProjetoRequisicoes(clientId, projectId as number),
    enabled: projectId !== null && projectId > 0,
    staleTime: 15_000,
  });
}

export function useLinkProjetoRequisicao(
  clientId: number,
  projectId: number,
): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rc) => linkProjetoRequisicao(clientId, projectId, rc),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKey(clientId, projectId) });
      // A contagem/lista do lado RC também muda.
      void qc.invalidateQueries({ queryKey: ['purchase-requests'] });
    },
  });
}

export function useUnlinkProjetoRequisicao(
  clientId: number,
  projectId: number,
): UseMutationResult<void, Error, number> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (rc) => unlinkProjetoRequisicao(clientId, projectId, rc),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKey(clientId, projectId) });
      void qc.invalidateQueries({ queryKey: ['purchase-requests'] });
    },
  });
}
